/**
 * Sign in.
 *
 * Three methods, and the page only offers what the server says it has:
 *
 * - **Magic link.** Always available, and deliberately the visually primary
 *   path: it is the one that works on every browser, every device and every
 *   account, including one that has never been signed into here before.
 *   Submitting the form asks the Worker to send one; the reply never says
 *   whether the address exists, which is deliberate (see `sendMagicLink`).
 * - **Passkey.** An accelerant for a returning brewer, not a replacement
 *   (PLAN-REMAINING P3). Rendered only when the Worker mounts the routes *and*
 *   this browser has WebAuthn — see `auth/passkeys.ts` on why both gates exist —
 *   and simply absent otherwise. Absent rather than disabled: a browser with no
 *   WebAuthn has nothing to say about it, and a greyed-out button explaining a
 *   platform limitation is noise on the one page a new user is guaranteed to
 *   see. The copy says what a passkey *is* in one sentence and then stops.
 * - **Google.** Rendered only when `/api/config` reports a credential. With no
 *   credential — the default for a local checkout — the button is replaced by a
 *   line of text explaining why, rather than by a button that would 500.
 *
 * ## The autofill ceremony
 *
 * When the browser supports conditional mediation, a passkey ceremony is started
 * on mount and left running against the email field, which carries
 * `autocomplete="email webauthn"`. A returning brewer then signs in by picking
 * their own address out of the dropdown they were about to type into — no
 * button, no email, no round trip. It resolves as `cancelled` the moment they
 * type instead, and that ending renders **nothing**: the ceremony was never
 * asked for, and an error message about abandoning something you did not start
 * is the worst kind of noise. The cleanup cancels it on navigation, because
 * WebAuthn permits one ceremony at a time and a stale one would break the next.
 *
 * ## Local development
 *
 * When the Worker reports `devEndpoints`, the link the dev `EmailSender`
 * captured is fetched and shown inline. That is the whole local sign-in story:
 * no mail server, no account, one click.
 */

import type { SearchSchemaInput } from '@tanstack/react-router';
import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { CheckIcon, KeyRoundIcon, LoaderIcon, MailIcon } from 'lucide-react';
import type { FormEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchDevMagicLink, sendMagicLink, signOut, startGoogleSignIn } from '@/auth/client';
import type { PasskeyFailure } from '@/auth/passkeys';
import {
  browserHasPasskeyAutofill,
  browserHasWebAuthn,
  cancelPasskeyCeremony,
  signInWithPasskey,
} from '@/auth/passkeys';
import { useSession } from '@/auth/session-context';
import { OpeningSplash, WORKSPACE_SPLASH_LABEL } from '@/components/opening-splash';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { parseReturnPath } from '@/lib/search-params';

/**
 * What a refused passkey sign-in says out loud.
 *
 * `cancelled` is absent from this map on purpose — the caller renders nothing
 * for it — and so is `unsupported`, which cannot be reached from a button that
 * is only drawn when WebAuthn exists. `unrecognised` is the interesting one: it
 * is what a passkey registered on a *different* account, or since deleted from
 * this one, produces, and the answer to it is the email field directly above.
 */
const SIGN_IN_MESSAGES: Partial<Record<PasskeyFailure, string>> = {
  'already-registered': 'That passkey is already registered here.',
  'stale-session': 'This passkey needs a fresh session. Use a sign-in link instead.',
  unauthorized: 'Use a sign-in link instead.',
  unrecognised: "That passkey isn't registered to an account here. Try a sign-in link.",
  unavailable: 'We could not reach the server. Check your connection.',
};

/**
 * `?next=` is where a signed-in brewer goes afterwards, for a page that needs
 * a session and sent them here: an invite (PLAN-COLLAB E2) is the first. It is
 * handed to the auth server as the callback, so it is held to a path on this
 * origin (`parseReturnPath`), and without one the page lands where it always
 * has.
 */
export interface SignInSearch {
  readonly next?: string | undefined;
}

export const Route = createFileRoute('/signin')({
  validateSearch: (search: { readonly next?: unknown } & SearchSchemaInput): SignInSearch => ({
    next: parseReturnPath(search.next),
  }),
  component: SignInPage,
});

type Phase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'sending' }
  | { readonly kind: 'sent'; readonly email: string; readonly devLink: string | null }
  | { readonly kind: 'error'; readonly message: string };

function SignInPage() {
  const session = useSession();
  const { next } = Route.useSearch();
  const router = useRouter();
  const callbackURL = next ?? '/';
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [googleBusy, setGoogleBusy] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  /** Both gates from `auth/passkeys.ts`: the server has it, and so does this browser. */
  const [passkeyReady, setPasskeyReady] = useState(false);

  const { user: signedInUser, config, refresh } = session;
  const passkeyOffered = config.providers.passkey;
  /** So the conditional ceremony is started once per mount, not once per render. */
  const conditionalStarted = useRef(false);

  useEffect(() => {
    if (signedInUser !== null || !passkeyOffered || !browserHasWebAuthn()) return;
    setPasskeyReady(true);
    if (conditionalStarted.current) return;
    conditionalStarted.current = true;

    // Started and then left running. It resolves when the brewer picks a passkey
    // out of the browser's own autofill menu — which may be in five seconds or
    // never — and `cancelled` is the ordinary ending. See the file header.
    let unmounted = false;
    void (async () => {
      if (!(await browserHasPasskeyAutofill()) || unmounted) return;
      const result = await signInWithPasskey({ autoFill: true });
      if (unmounted || !result.ok) return;
      await refresh();
    })();

    return () => {
      unmounted = true;
      void cancelPasskeyCeremony();
    };
  }, [signedInUser, passkeyOffered, refresh]);

  /**
   * The explicit button: a modal ceremony, for a brewer who came here to use a
   * passkey rather than one who noticed it in a dropdown.
   *
   * Silence on `cancelled` for the same reason the autofill path is silent about
   * it — closing the system dialog is a decision, not a fault — and the message
   * map handles everything else. There is no `refresh()` on failure because
   * nothing changed.
   */
  const passkey = useCallback(async () => {
    setPasskeyError(null);
    setPasskeyBusy(true);
    const result = await signInWithPasskey();
    setPasskeyBusy(false);
    if (result.ok) {
      await refresh();
      return;
    }
    setPasskeyError(SIGN_IN_MESSAGES[result.reason] ?? null);
  }, [refresh]);

  const submit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const address = email.trim();
      if (address.length === 0) return;

      setPhase({ kind: 'sending' });
      const result = await sendMagicLink(address, callbackURL);
      if (!result.ok) {
        setPhase({ kind: 'error', message: result.message });
        return;
      }
      // Only ever populated by a Worker running with dev auth enabled.
      const devLink = session.config.devEndpoints ? await fetchDevMagicLink(address) : null;
      setPhase({ kind: 'sent', email: address, devLink });
    },
    [email, session.config.devEndpoints, callbackURL],
  );

  const google = useCallback(async () => {
    setGoogleBusy(true);
    const result = await startGoogleSignIn(callbackURL);
    setGoogleBusy(false);
    if (!result.ok) {
      setPhase({ kind: 'error', message: result.message });
      return;
    }
    window.location.assign(result.value);
  }, [callbackURL]);

  // Signed in with somewhere to go back to: a passkey, or a session that was
  // already here when the page that needed one sent the brewer over. A magic
  // link and Google land on `next` themselves, as their callback.
  const returning = session.user !== null && next !== undefined;
  useEffect(() => {
    if (returning && next !== undefined) router.history.replace(next);
  }, [returning, next, router]);

  // On its way back to `next`: the splash, rather than a frame of "You are
  // signed in" that the replace above is about to take away.
  if (returning) return <OpeningSplash label={WORKSPACE_SPLASH_LABEL} />;

  // `offline` counts as signed in here, and has to: it means this device has an
  // identity and the server merely cannot be reached (see `session-context.tsx`).
  // Offering the sign-in form instead would be both wrong and futile — every
  // method on it is a round trip to the server that is not answering.
  if (session.user !== null) {
    return (
      <Centered>
        <h1 className="text-2xl font-semibold tracking-tight">You are signed in</h1>
        <p className="text-sm text-muted-foreground">
          As <span className="font-medium text-foreground">{session.user.email}</span>.{' '}
          {session.status === 'offline'
            ? 'Your workspace is on this device and syncs when the connection comes back.'
            : 'Your workspace syncs to every device you sign in on.'}
        </p>
        <div className="mt-2 flex gap-2">
          <Button asChild variant="default">
            <Link to="/recipes">Go to recipes</Link>
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              await signOut();
              await session.refresh();
            }}
          >
            Sign out
          </Button>
        </div>
      </Centered>
    );
  }

  return (
    <Centered>
      <h1 className="text-2xl font-semibold tracking-tight">Sign in to Beerwright</h1>
      <p className="text-sm text-muted-foreground">
        Signing in syncs your recipes across devices. You can keep using Beerwright without an
        account, and everything stays on this device.
      </p>

      {phase.kind === 'sent' ? (
        <div className="mt-4 flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
          <p className="flex items-center gap-2 text-sm font-medium">
            <CheckIcon className="size-4 text-primary" aria-hidden="true" />
            Check {phase.email}
          </p>
          <p className="text-xs text-muted-foreground">
            If we can find or create an account for that address, we've sent you a sign-in link. It
            expires in 15 minutes.
          </p>
          {phase.devLink !== null && (
            <div className="flex flex-col gap-1 rounded-md bg-muted/60 p-3">
              <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                Development
              </p>
              <a
                href={phase.devLink}
                data-testid="dev-magic-link"
                className="text-sm break-all text-primary underline underline-offset-2"
              >
                Open your sign-in link
              </a>
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={() => setPhase({ kind: 'idle' })}>
            Use a different address
          </Button>
        </div>
      ) : (
        <form className="mt-4 flex flex-col gap-3" onSubmit={(event) => void submit(event)}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="signin-email">Email</Label>
            <Input
              id="signin-email"
              name="email"
              type="email"
              required
              // The `webauthn` token is what makes the conditional ceremony
              // legal: without an input advertising it, the browser has nowhere
              // to hang the passkey entry and `startAuthentication` refuses to
              // run in autofill mode. Harmless on every browser that has never
              // heard of it — unknown autocomplete tokens are ignored.
              autoComplete="email webauthn"
              placeholder="you@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <Button type="submit" disabled={phase.kind === 'sending'}>
            {phase.kind === 'sending' ? (
              <LoaderIcon className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <MailIcon className="size-4" aria-hidden="true" />
            )}
            Email me a sign-in link
          </Button>
          {phase.kind === 'error' && (
            <p role="alert" className="text-sm text-destructive">
              {phase.message}
            </p>
          )}
        </form>
      )}

      <div className="mt-6 flex flex-col gap-2 border-t border-border pt-6">
        {passkeyReady && (
          <>
            <Button
              variant="outline"
              data-testid="passkey-signin"
              onClick={() => void passkey()}
              disabled={passkeyBusy}
            >
              {passkeyBusy ? (
                <LoaderIcon className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <KeyRoundIcon className="size-4" aria-hidden="true" />
              )}
              Sign in with a passkey
            </Button>
            {/* One sentence, and it describes the mechanism rather than selling
                it. Somebody who already uses passkeys does not need the pitch,
                and somebody who does not needs to know what pressing the button
                will make their device do. */}
            <p className="text-xs text-muted-foreground">
              A passkey signs you in with this device's fingerprint, face or screen lock. You can
              add one from Settings once you are signed in.
            </p>
            {passkeyError !== null && (
              <p role="alert" className="text-sm text-destructive">
                {passkeyError}
              </p>
            )}
          </>
        )}
        {session.config.providers.google ? (
          <Button variant="outline" onClick={() => void google()} disabled={googleBusy}>
            {googleBusy ? (
              <LoaderIcon className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <GoogleIcon />
            )}
            Continue with Google
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">
            Google sign-in isn’t set up on this server yet. Set{' '}
            <code className="font-mono">GOOGLE_CLIENT_ID</code> and{' '}
            <code className="font-mono">GOOGLE_CLIENT_SECRET</code> to enable it.
          </p>
        )}
      </div>
    </Centered>
  );
}

function GoogleIcon() {
  return (
    <svg className="size-4" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function Centered({ children }: { readonly children: React.ReactNode }) {
  return (
    <div className="mx-auto flex min-h-full w-full max-w-md flex-col justify-center gap-2 px-4 py-8 sm:px-8 sm:py-10">
      {children}
    </div>
  );
}
